import { FunctionComponent, useEffect, useState } from 'react';
import { InputGroup, Button, Form } from 'react-bootstrap';

export interface NumericStepperProps
{
    field: string;
    value?: number;
    min?: number;
    max?: number;
    step?: number;
    isEnabled?: boolean;
    placeholder?: string;
    onValueChanged?: (field: string, value: number | null) => void;
}

/**
 * Component: NumericStepper - A numeric input with visible increment/decrement buttons.
 * Handles integer values with min/max constraints and step functionality.
 */
const NumericStepper: FunctionComponent<NumericStepperProps> = (props) =>
{
    const [internalValue, setInternalValue] = useState<string>('');

    /**
     * Initialize internal value from props
     */
    useEffect(() =>
    {
        if (props.value !== undefined && props.value !== null)
        {
            setInternalValue(props.value.toString());
        }
        else
        {
            setInternalValue('');
        }
    }, [props.value]);

    /**
     * Handle value change
     */
    const handleValueChange = (newValue: number | null) =>
    {
        if (newValue === null)
        {
            setInternalValue('');
            props.onValueChanged?.(props.field, null);
            
            return;
        }

        // Apply min/max constraints
        let constrainedValue = newValue;
        if (props.min !== undefined && constrainedValue < props.min)
        {
            constrainedValue = props.min;
        }
        if (props.max !== undefined && constrainedValue > props.max)
        {
            constrainedValue = props.max;
        }

        setInternalValue(constrainedValue.toString());
        props.onValueChanged?.(props.field, constrainedValue);
    };

    /**
     * Handle increment button click
     */
    const handleIncrement = () =>
    {
        let currentValue = internalValue === '' ? 0 : parseInt(internalValue);
        let step = props.step ?? 1;
        handleValueChange(currentValue + step);
    };

    /**
     * Handle decrement button click
     */
    const handleDecrement = () =>
    {
        let currentValue = internalValue === '' ? 0 : parseInt(internalValue);
        let step = props.step ?? 1;
        handleValueChange(currentValue - step);
    };

    /**
     * Handle direct input change
     */
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    {
        let value = e.target.value;
        
        // Allow empty value
        if (value === '')
        {
            setInternalValue('');
            props.onValueChanged?.(props.field, null);
            
            return;
        }

        // Only allow valid integers
        let intValue = parseInt(value);
        if (!isNaN(intValue))
        {
            handleValueChange(intValue);
        }
    };

    /**
     * Handle keyboard events
     */
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
    {
        if (e.key === 'ArrowUp')
        {
            e.preventDefault();
            handleIncrement();
        }
        else if (e.key === 'ArrowDown')
        {
            e.preventDefault();
            handleDecrement();
        }
    };

    /**
     * Prevent scroll wheel from changing value
     */
    const handleWheel = (e: React.WheelEvent<HTMLInputElement>) =>
    {
        e.currentTarget.blur();
    };

    let isEnabled = props.isEnabled ?? true;
    let currentValue = internalValue === '' ? null : parseInt(internalValue);
    
    // Determine if buttons should be disabled
    let isDecrementDisabled = !isEnabled || (props.min !== undefined && currentValue !== null && currentValue <= props.min);
    let isIncrementDisabled = !isEnabled || (props.max !== undefined && currentValue !== null && currentValue >= props.max);

    /** Render */
    return (
        <InputGroup>
            <Button
                variant="outline-secondary"
                disabled={isDecrementDisabled}
                onClick={handleDecrement}
                tabIndex={-1}
            >
                −
            </Button>
            <Form.Control
                type="number"
                value={internalValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onWheel={handleWheel}
                disabled={!isEnabled}
                placeholder={props.placeholder}
                className="text-center"
                style={{ appearance: 'textfield' }}
            />
            <Button
                variant="outline-secondary"
                disabled={isIncrementDisabled}
                onClick={handleIncrement}
                tabIndex={-1}
            >
                +
            </Button>
        </InputGroup>
    );
}

export default NumericStepper;